import React, { useCallback } from 'react';
import { HistoricalLayersService, HistoricalTileSource } from '../../../types/historical-layers';
import Mission from '../../../db/MissionClass';
import { BaseHistoricalLayerSelector } from './BaseHistoricalLayerSelector';

/**
 * Props for the MissionHistoricalLayerSelector component
 */
export interface MissionHistoricalLayerSelectorProps {
  /**
   * Currently selected year
   */
  selectedYear: number;

  /**
   * Callback when year selection changes
   */
  onYearChange: (year: number, tileSource: HistoricalTileSource) => void;

  /**
   * Service for managing historical layers
   */
  historicalLayersService: HistoricalLayersService;

  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;

  /**
   * Current mission for mission-based fetching
   */
  mission?: Mission;
}

/**
 * Mission-based version of HistoricalLayerSelector
 * This version fetches historical imagery based on the current mission's location
 * instead of using map center and bounds supplied by the parent component
 */
export const MissionHistoricalLayerSelector: React.FC<MissionHistoricalLayerSelectorProps> = ({
  selectedYear,
  onYearChange,
  historicalLayersService,
  disabled = false,
  mission,
}) => {
  // Handle fetch data based on mission location
  const handleFetchData = useCallback(
    async (forceRefresh: boolean = false) => {
      if (!mission) {
        throw new Error('No mission selected');
      }

      // Load historical map layers for the mission
      await mission.loadHistoricalMapLayers(historicalLayersService, forceRefresh);
    },
    [mission, historicalLayersService],
  );

  return (
    <BaseHistoricalLayerSelector
      selectedYear={selectedYear}
      onYearChange={onYearChange}
      historicalLayersService={historicalLayersService}
      disabled={disabled}
      onFetchData={handleFetchData}
      canFetch={!!mission}
      noDataMessage="No historical imagery available for this mission"
      noDataSubMessage={mission ? 'Click refresh to load imagery' : undefined}
      fetchTooltip="Fetch historical imagery for this mission"
    />
  );
};
